// ---------------------------------------------------------------------------
// Editor Shell — 3-column layout:
//   Left: EventHierarchyTree (240px)
//   Center: FlowStepPanel (flex-grow)
//   Right: StepInspector (320px, toggleable)
// ---------------------------------------------------------------------------

import { useEffect } from 'react'
import { EventHierarchyTree } from '@/features/flow-editor/components/events/event-hierarchy-tree'
import { FlowStepPanel } from '@/features/flow-editor/components/flows/flow-step-panel'
import { StepInspector } from '@/features/flow-editor/components/inspector/step-inspector'
import { StatusBar } from '@/features/flow-editor/components/shell/status-bar'
import { Toaster } from '@/features/flow-editor/components/shell/toaster'
import { useShortcuts } from '@/features/flow-editor/hooks/use-shortcuts'
import { useFlowEditorStore } from '@/features/flow-editor/store/flow-editor-store'

export interface EditorShellProps {
  embedded?: boolean
}

export function EditorShell({ embedded = false }: EditorShellProps) {
  useShortcuts()
  const inspectorOpen = useFlowEditorStore((s) => s.inspectorOpen)
  const selectedStepIndex = useFlowEditorStore((s) => s.selectedStepIndex)

  // Auto-open inspector when a step is selected
  useEffect(() => {
    if (selectedStepIndex !== null) {
      useFlowEditorStore.getState().setInspectorOpen(true)
    }
  }, [selectedStepIndex])

  return (
    <div className="flex flex-col h-full w-full overflow-hidden bg-background text-foreground">
      <div className="flex-1 flex overflow-hidden">
        {/* Left panel: Event hierarchy tree (240px) */}
        <div className="w-[240px] shrink-0 border-r border-border overflow-y-auto bg-background">
          <EventHierarchyTree />
        </div>
        {/* Center panel: Flow step cards (flex-grow) */}
        <div className="flex-1 min-w-0">
          <FlowStepPanel />
        </div>
        {/* Right panel: Step Inspector (320px, toggleable) */}
        <div
          className="shrink-0 border-l border-border bg-background overflow-hidden transition-[width] duration-150 ease-out"
          style={{ width: inspectorOpen ? 320 : 0 }}
        >
          <div className="w-[320px] h-full overflow-y-auto">
            <StepInspector />
          </div>
        </div>
      </div>
      {!embedded && <StatusBar />}
      {!embedded && <Toaster />}
    </div>
  )
}
